import React, { useEffect, useState } from 'react';
import { inventoriesAPI, stockMovementsAPI, sitesAPI, industrialProductsAPI, supplierProductsAPI, suppliersAPI, inventoryFormTemplatesAPI, exportAPI, downloadAuthFile, openAuthPdf } from '@/lib/api';
import { getInventoryTypeLabel, getMovementTypeLabel, getCurrentPeriod, getPeriodOptions, formatDate, formatCurrency, supplierShortcutLabel } from '@/lib/utils';
import {
    calculateZoneTotal,
    calculateLineTotal,
    calculateEffectiveUnitPrice,
    calculateEffectiveQuantity,
    calculateEffectiveZoneQuantities,
    getInvItemData as getInvItemDataPure,
    getGroupedInventoryData as getGroupedInventoryDataPure,
    resolveInventoryUnits,
} from '@/lib/inventory';
import { UnitToggle, MobileUnitToggle } from '@/components/inventory/UnitToggle';
import { QtyCell, MobileQtyCell } from '@/components/inventory/QtyCell';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import InventoryFormsPage from '@/pages/InventoryFormsPage';
import { 
    Warehouse, 
    Plus, 
    Download, 
    Trash2,
    PackagePlus,
    ClipboardList,
    Pencil,
    Eye,
    FileText,
    BarChart3,
    ChevronDown
} from 'lucide-react';
import ConsumptionTab from '@/components/ConsumptionTab';
import InventoryDialogs from '@/pages/inventory/InventoryDialogs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

export default function InventoryPage() {
    const { hasPermission, user } = useAuth();
    const canCreate = hasPermission('inventories_create');
    const canEdit = hasPermission('inventories_edit');
    const canDelete = hasPermission('inventories_delete');
    const canManageInventoryForms = hasPermission('inventory_forms_manage');
    
    // Filtrer les sites selon les sites autorisés de l'utilisateur
    const getAllowedSites = (allSites) => {
        if (!user?.allowed_sites || user.allowed_sites.length === 0) {
            return allSites; // Accès à tous les sites
        }
        return allSites.filter(site => user.allowed_sites.includes(site.id));
    };
    
    const [inventories, setInventories] = useState([]);
    const [movements, setMovements] = useState([]);
    const [sites, setSites] = useState([]);
    const [products, setProducts] = useState([]);
    const [supplierProducts, setSupplierProducts] = useState([]);
    const [suppliers, setSuppliers] = useState([]);    const [inventoryFormTemplates, setInventoryFormTemplates] = useState([]);
    const [receivedProductIds, setReceivedProductIds] = useState(null); // null = not loaded yet, [] = loaded but empty
    const [loading, setLoading] = useState(true);
    
    const [filterSite, setFilterSite] = useState('all');
    const [filterPeriod, setFilterPeriod] = useState(getCurrentPeriod());
    const [filterProduct, setFilterProduct] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterFamily, setFilterFamily] = useState('all');
    const [filterCompany, setFilterCompany] = useState('all');
    const [filterSupplier, setFilterSupplier] = useState('all');
    // Editable supplier shortcuts (persisted per browser, distinct from Finance)
    const STOCK_DEFAULT_SUPPLIER_SHORTCUTS = ['Vodelys', 'MG Store', 'Asie Caraibes', 'Le Moulin', 'S.A.P.Y.'];
    const [supplierShortcutNames, setSupplierShortcutNames] = useState(() => {
        try {
            const raw = localStorage.getItem('inventoryPage_supplierShortcuts');
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) return parsed;
            }
        } catch { /* ignore */ }
        return STOCK_DEFAULT_SUPPLIER_SHORTCUTS;
    });
    const [showShortcutEditor, setShowShortcutEditor] = useState(false);
    useEffect(() => {
        try {
            localStorage.setItem('inventoryPage_supplierShortcuts', JSON.stringify(supplierShortcutNames));
        } catch { /* ignore */ }
    }, [supplierShortcutNames]);
    
    // Dialogs
    const [inventoryDialog, setInventoryDialog] = useState(false);
    const [receptionDialog, setReceptionDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [deleteInventoryDialog, setDeleteInventoryDialog] = useState(false);
    const [movementToDelete, setMovementToDelete] = useState(null);
    const [inventoryToDelete, setInventoryToDelete] = useState(null);
    const [editingInventory, setEditingInventory] = useState(null);
    const [inventoryDetailDialog, setInventoryDetailDialog] = useState(false);
    const [selectedInventoryGroup, setSelectedInventoryGroup] = useState(null);
    const [newInventoryDialog, setNewInventoryDialog] = useState(false);
    const [newInventoryForm, setNewInventoryForm] = useState({ site_id: '', period: getCurrentPeriod(), inventory_type: 'final' });
    
    // Delete group confirmation states
    const [deleteGroupDialog, setDeleteGroupDialog] = useState(false);
    const [deleteGroupConfirmDialog, setDeleteGroupConfirmDialog] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [addProductExpanded, setAddProductExpanded] = useState(false);
    
    // Inventory form PDF dialog
    const [inventoryFormPdfDialog, setInventoryFormPdfDialog] = useState(false);
    const [pdfFormSiteId, setPdfFormSiteId] = useState('');
    const [pdfFormPeriod, setPdfFormPeriod] = useState(getCurrentPeriod());
    
    // Multi-product inventory form
    const [inventoryForm, setInventoryForm] = useState({
        site_id: '',
        inventory_type: 'initial',
        period: getCurrentPeriod(),
        items: []
    });
    const [inventoryItem, setInventoryItem] = useState({
        industrial_product_id: '',
        supplier_product_id: '',
        quantity_reserve: '',
        quantity_stand: '',
        quantity_siege: '',
        input_unit: 'base' // 'base' = unité produit (kg/L/pièce), 'purchase' = unité d'achat
    });
    
    // Detail dialog add item
    const [detailAddItem, setDetailAddItem] = useState({
        industrial_product_id: '',
        supplier_product_id: '',
        quantity_reserve: '',
        quantity_stand: '',
        quantity_siege: '',
        input_unit: 'base'
    });
    
    const [receptionForm, setReceptionForm] = useState({
        site_id: '',
        industrial_product_id: '',
        supplier_product_id: '',
        quantity: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
    });

    const periodOptions = getPeriodOptions();
    
    // Get unique categories from products
    const categories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();
    
    // Get unique families from products
    const families = [...new Set(products.map(p => p.famille).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));

    const getProductFamily = (productId) => {
        const product = products.find(p => p.id === productId);
        return product?.famille || '';
    };

    useEffect(() => {
        loadData();
    }, []);

    // Merge persisted inventory changes into the currently open group WITHOUT
    // wiping the not-yet-persisted "seed" rows (added by the new-inventory
    // pre-population flow). The previous implementation replaced items with
    // the filtered `inventories` list, which dropped every seed the first
    // time the user saved a single line.
    useEffect(() => {
        if (!selectedInventoryGroup || !inventoryDetailDialog) return;
        const updatedInventories = inventories.filter(inv => (
            inv.site_id === selectedInventoryGroup.site_id
            && inv.period === selectedInventoryGroup.period
            && inv.inventory_type === selectedInventoryGroup.inventory_type
        ));
        setSelectedInventoryGroup(prev => {
            if (!prev) return prev;
            const site = sites.find(s => s.id === prev.site_id);
            // Refresh existing items (replace with fresh persisted data when
            // available; otherwise keep the seed row as-is).
            const refreshedItems = prev.items.map(it => {
                if (it.id) {
                    const fresh = updatedInventories.find(u => u.id === it.id);
                    return fresh || it;
                }
                const fresh = updatedInventories.find(u => (
                    u.industrial_product_id === it.industrial_product_id
                    && (u.supplier_product_id || '') === (it.supplier_product_id || '')
                ));
                return fresh ? { ...it, ...fresh } : it;
            });
            // Append any persisted item that isn't already in the group
            // (e.g. line created via a different code path).
            const existingKeys = new Set(refreshedItems.map(it => (
                it.id
                    ? `id:${it.id}`
                    : `sp:${it.industrial_product_id}-${it.supplier_product_id || 'none'}`
            )));
            const appended = updatedInventories.filter(u => (
                !existingKeys.has(`id:${u.id}`)
                && !existingKeys.has(`sp:${u.industrial_product_id}-${u.supplier_product_id || 'none'}`)
            ));
            const merged = [...refreshedItems, ...appended];
            return {
                site_id: prev.site_id,
                site_name: site?.name || prev.site_name,
                period: prev.period,
                inventory_type: prev.inventory_type,
                items: merged,
                total_items: merged.length,
            };
        });
    }, [inventories]);

    const loadData = async () => {
        try {
            const [invRes, movRes, sitesRes, prodRes, spRes, supRes, tplRes, receivedRes] = await Promise.all([
                inventoriesAPI.getAll(),
                stockMovementsAPI.getAll(),
                sitesAPI.getAll(),
                industrialProductsAPI.getAll(),
                supplierProductsAPI.getAll(),
                suppliersAPI.getAll(),
                inventoryFormTemplatesAPI.getAll().catch(() => ({ data: [] })),
                inventoriesAPI.getReceivedProductIds().catch(() => ({ data: { product_ids: [] } })),
            ]);
            setInventories(invRes.data);
            setMovements(movRes.data);
            // Appliquer le filtre des sites autorisés
            const allowedSites = getAllowedSites(sitesRes.data);
            setSites(allowedSites);
            setProducts(prodRes.data);
            setSupplierProducts(spRes.data);
            setSuppliers(supRes.data || []);
            setInventoryFormTemplates(tplRes.data || []);
            setReceivedProductIds(receivedRes.data?.product_ids || []);

            // If the default period (current month) has no inventories for the
            // user's allowed sites, jump to the most recent period that does.
            // Only override when the user hasn't already picked something —
            // detected by comparing against the initial "current period" value.
            try {
                const allowedIds = new Set(allowedSites.map(s => s.id));
                const isRestricted = user?.allowed_sites && user.allowed_sites.length > 0;
                const relevantInvs = isRestricted
                    ? invRes.data.filter(i => allowedIds.has(i.site_id))
                    : invRes.data;
                const currentInitial = getCurrentPeriod();
                if (filterPeriod === currentInitial) {
                    const hasCurrent = relevantInvs.some(i => i.period === currentInitial);
                    if (!hasCurrent && relevantInvs.length > 0) {
                        const periods = [...new Set(relevantInvs.map(i => i.period).filter(Boolean))].sort();
                        const latest = periods[periods.length - 1];
                        if (latest) setFilterPeriod(latest);
                    }
                }
            } catch { /* non-blocking UX helper */ }
        } catch (error) {
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    // Get category for an inventory item
    const getProductCategory = (productId) => {
        const product = products.find(p => p.id === productId);
        return product?.category || '';
    };

    // Company → site mapping (mirrors Commandes / Finance shortcut bar)
    const COMPANY_MATCHERS = {
        'Fuji Express': (name) => (name || '').startsWith('FE '),
        'Concept Lab': (name) => name === 'Concept Lab',
        'My Food': (name) => name === 'My Food',
        'Boucherie': (name) => name === 'Boucherie',
    };
    const getSiteIdsForCompany = (companyName) => {
        const matcher = COMPANY_MATCHERS[companyName];
        if (!matcher) return [];
        return sites.filter(s => matcher(s.name)).map(s => s.id);
    };
    const normalizeStr = (s) => (s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
    const resolveShortcutSupplier = (name) => {
        const n = normalizeStr(name);
        if (!n) return null;
        let match = suppliers.find(s => normalizeStr(s.name) === n);
        if (!match) match = suppliers.find(s => normalizeStr(s.name).startsWith(n));
        if (!match) match = suppliers.find(s => normalizeStr(s.name).includes(n));
        return match || null;
    };
    // Map: industrial_product_id -> Set(supplier_id) via supplier_products
    const productToSupplierIds = (() => {
        const map = new Map();
        for (const sp of supplierProducts || []) {
            if (!sp.industrial_product_id || !sp.supplier_id) continue;
            if (!map.has(sp.industrial_product_id)) map.set(sp.industrial_product_id, new Set());
            map.get(sp.industrial_product_id).add(sp.supplier_id);
        }
        return map;
    })();
    const inventoryMatchesSupplier = (inv) => {
        if (filterSupplier === 'all') return true;
        // Prefer direct supplier_product_id (resolves to supplier_id), else fall back to industrial product
        if (inv.supplier_product_id) {
            const sp = (supplierProducts || []).find(p => p.id === inv.supplier_product_id);
            if (sp) return sp.supplier_id === filterSupplier;
        }
        const ids = productToSupplierIds.get(inv.industrial_product_id);
        return ids ? ids.has(filterSupplier) : false;
    };

    // Vérifier si un site est autorisé pour l'utilisateur
    const isSiteAllowed = (siteId) => {
        if (!user?.allowed_sites || user.allowed_sites.length === 0) {
            return true; // Accès à tous les sites
        }
        return user.allowed_sites.includes(siteId);
    };

    const filteredInventories = inventories
        .filter(inv => {
            // Filtrer par site autorisé
            if (!isSiteAllowed(inv.site_id)) return false;
            const matchesSite = filterSite === 'all' || inv.site_id === filterSite;
            const matchesCompany = filterCompany === 'all' || getSiteIdsForCompany(filterCompany).includes(inv.site_id);
            const matchesPeriod = inv.period === filterPeriod;
            const matchesProduct = filterProduct === 'all' || inv.industrial_product_id === filterProduct;
            const productCategory = getProductCategory(inv.industrial_product_id);
            const matchesCategory = filterCategory === 'all' || productCategory === filterCategory;
            const matchesSupplier = inventoryMatchesSupplier(inv);
            return matchesSite && matchesCompany && matchesPeriod && matchesProduct && matchesCategory && matchesSupplier;
        })
        .sort((a, b) => {
            const catA = getProductCategory(a.industrial_product_id) || '';
            const catB = getProductCategory(b.industrial_product_id) || '';
            if (catA !== catB) {
                return catA.localeCompare(catB);
            }
            return (a.product_name || '').localeCompare(b.product_name || '');
        });

    const filteredMovements = movements.filter(mov => {
        if (!isSiteAllowed(mov.site_id)) return false;
        const matchesSite = filterSite === 'all' || mov.site_id === filterSite;
        const matchesCompany = filterCompany === 'all' || getSiteIdsForCompany(filterCompany).includes(mov.site_id);
        const matchesPeriod = mov.date.startsWith(filterPeriod);
        const matchesProduct = filterProduct === 'all' || mov.industrial_product_id === filterProduct;
        const productCategory = getProductCategory(mov.industrial_product_id);
        const matchesCategory = filterCategory === 'all' || productCategory === filterCategory;
        const productFamily = getProductFamily(mov.industrial_product_id);
        const matchesFamily = filterFamily === 'all' || productFamily === filterFamily;
        const matchesSupplier = inventoryMatchesSupplier(mov);
        return matchesSite && matchesCompany && matchesPeriod && matchesProduct && matchesCategory && matchesFamily && matchesSupplier;
    });

    // Products sorted by category then by name for dropdowns
    const sortedProducts = [...products].sort((a, b) => {
        const catA = a.category || '';
        const catB = b.category || '';
        if (catA !== catB) return catA.localeCompare(catB);
        return a.name.localeCompare(b.name);
    });

    // Group inventories by site, period, and type for card display
    const groupedInventories = filteredInventories.reduce((acc, inv) => {
        const key = `${inv.site_id}-${inv.period}-${inv.inventory_type}`;
        if (!acc[key]) {
            acc[key] = {
                site_id: inv.site_id,
                site_name: inv.site_name,
                period: inv.period,
                inventory_type: inv.inventory_type,
                items: [],
                total_items: 0
            };
        }
        acc[key].items.push(inv);
        acc[key].total_items++;
        return acc;
    }, {});
    const inventoryGroups = Object.values(groupedInventories).sort((a, b) => {
        // Sort by period desc, then by site name
        if (a.period !== b.period) return b.period.localeCompare(a.period);
        return a.site_name.localeCompare(b.site_name);
    });

    const getSupplierProductsForProduct = (productId) => {
        return supplierProducts.filter(sp => sp.industrial_product_id === productId);
    };

    // Calculate total from zones
    // Add item to inventory form
    const handleAddInventoryItem = () => {
        const totalQty = calculateZoneTotal(inventoryItem.quantity_reserve, inventoryItem.quantity_stand, inventoryItem.quantity_siege);
        
        if (!inventoryItem.industrial_product_id || totalQty <= 0) {
            toast.error('Sélectionnez un produit et saisissez au moins une quantité');
            return;
        }
        
        const product = products.find(p => p.id === inventoryItem.industrial_product_id);
        const sp = supplierProducts.find(p => p.id === inventoryItem.supplier_product_id);
        
        const newItem = {
            industrial_product_id: inventoryItem.industrial_product_id,
            supplier_product_id: inventoryItem.supplier_product_id || '',
            quantity: totalQty,
            quantity_reserve: parseFloat(inventoryItem.quantity_reserve) || 0,
            quantity_stand: parseFloat(inventoryItem.quantity_stand) || 0,
            quantity_siege: parseFloat(inventoryItem.quantity_siege) || 0,
            product_name: product?.name || 'Inconnu',
            product_unit: (() => { const { baseUnit } = resolveInventoryUnits(sp, product); return baseUnit; })(),
            supplier_product_name: sp?.supplier_name_product || '',
            unit_price: sp?.unit_price || 0,
            capacity_value: sp?.capacity_value || null,
            capacity_unit: sp?.capacity_unit || null,
            // Purchase-unit label ("Paquet", "Carton", …) shown in the "Unité"
            // picker. Uses the shared helper so pack products (Poche in a Carton)
            // display the outer unit (Carton) instead of the billing unit.
            purchase_unit: (() => { const { purchaseUnitLabel } = resolveInventoryUnits(sp, product); return purchaseUnitLabel; })(),
            input_unit: inventoryItem.input_unit,
            category: product?.category || ''
        };
        
        setInventoryForm(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));
        
        setInventoryItem({ industrial_product_id: '', supplier_product_id: '', quantity_reserve: '', quantity_stand: '', quantity_siege: '', input_unit: 'base' });
    };

    const handleRemoveInventoryItem = (index) => {
        setInventoryForm(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    // Calculate line total based on unit type
    // (See lib/inventory.js for the pure implementation.)

    // Get display unit for an item
    const getDisplayUnit = (item) => {
        if (item.input_unit === 'purchase') {
            return item.purchase_unit || 'unité achat';
        }
        return item.product_unit || 'pièce';
    };

    // Calculate the effective unit price to save based on input unit
    // We always save the supplier's unit_price (price per purchase unit/pack)
    // (See lib/inventory.js)

    // Calculate the quantity to save (always in base units)
    // (See lib/inventory.js)

    // Calculate effective zone quantities
    // (See lib/inventory.js)

    // Apply a form template: prefill the inventory form items from the template's items.
    // Each template item contains an industrial_product_id + optional supplier_product_ids list.
    const handleApplyTemplate = (templateId) => {
        const tpl = inventoryFormTemplates.find(t => t.id === templateId);
        if (!tpl || !tpl.items || tpl.items.length === 0) {
            toast.error('Modele vide ou introuvable');
            return;
        }
        const newItems = [];
        tpl.items.forEach(ti => {
            const prod = products.find(p => p.id === ti.industrial_product_id);
            if (!prod) return;
            const spIds = Array.isArray(ti.supplier_product_ids) && ti.supplier_product_ids.length > 0
                ? ti.supplier_product_ids
                : [null];
            spIds.forEach(spId => {
                const sp = spId ? supplierProducts.find(s => s.id === spId) : null;
                newItems.push({
                    industrial_product_id: prod.id,
                    product_name: prod.name,
                    supplier_product_id: sp ? sp.id : '',
                    supplier_product_name: sp ? (sp.supplier_name_product || sp.supplier_ref) : '',
                    quantity: 0,
                    quantity_reserve: 0,
                    quantity_stand: 0,
                    quantity_siege: 0,
                    unit_price: sp?.unit_price ?? prod?.average_price ?? 0,
                    zone_mode: 'single',
                });
            });
        });
        if (newItems.length === 0) {
            toast.error('Aucun produit du modele n\'a pu etre ajoute');
            return;
        }
        setInventoryForm(prev => ({ ...prev, items: newItems }));
        toast.success(`${newItems.length} produit(s) ajoute(s) depuis "${tpl.name}"`);
    };

    // Apply a template directly from the detail dialog (unified flow):
    // creates inventory rows with qty=0 for each template item, then reloads.
    const handleApplyTemplateFromDetail = async (templateId) => {
        if (!selectedInventoryGroup) return;
        const tpl = inventoryFormTemplates.find(t => t.id === templateId);
        if (!tpl || !tpl.items || tpl.items.length === 0) {
            toast.error('Modèle vide ou introuvable');
            return;
        }
        try {
            const payloads = [];
            tpl.items.forEach(ti => {
                const prod = products.find(p => p.id === ti.industrial_product_id);
                if (!prod) return;
                const spIds = Array.isArray(ti.supplier_product_ids) && ti.supplier_product_ids.length > 0
                    ? ti.supplier_product_ids
                    : [null];
                spIds.forEach(spId => {
                    const sp = spId ? supplierProducts.find(s => s.id === spId) : null;
                    payloads.push({
                        site_id: selectedInventoryGroup.site_id,
                        industrial_product_id: prod.id,
                        supplier_product_id: sp ? sp.id : '',
                        quantity: 0,
                        quantity_reserve: 0,
                        quantity_stand: 0,
                        quantity_siege: 0,
                        inventory_type: selectedInventoryGroup.inventory_type,
                        period: selectedInventoryGroup.period,
                        unit_price: sp?.unit_price ?? prod?.average_price ?? 0,
                    });
                });
            });
            if (payloads.length === 0) {
                toast.error('Aucun produit du modèle n\'a pu être ajouté');
                return;
            }
            await Promise.all(payloads.map(p => inventoriesAPI.create(p)));
            toast.success(`${payloads.length} produit(s) ajouté(s) depuis "${tpl.name}"`);
            await loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'application du modèle');
        }
    };

    const handleSaveInventory = async () => {
        if (editingInventory) {
            // Single item edit mode
            if (!inventoryForm.site_id || inventoryForm.items.length === 0) {
                toast.error('Site et au moins un produit sont requis');
                return;
            }
            
            try {
                const item = inventoryForm.items[0];
                const effectiveQuantity = calculateEffectiveQuantity(item);
                const effectiveUnitPrice = calculateEffectiveUnitPrice(item);
                const zoneQtys = calculateEffectiveZoneQuantities(item);
                
                await inventoriesAPI.update(editingInventory.id, {
                    site_id: inventoryForm.site_id,
                    industrial_product_id: item.industrial_product_id,
                    supplier_product_id: item.supplier_product_id,
                    quantity: effectiveQuantity,
                    quantity_reserve: zoneQtys.quantity_reserve,
                    quantity_stand: zoneQtys.quantity_stand,
                    quantity_siege: zoneQtys.quantity_siege,
                    inventory_type: inventoryForm.inventory_type,
                    period: inventoryForm.period,
                    unit_price: effectiveUnitPrice
                });
                toast.success('Inventaire modifié');
                setInventoryDialog(false);
                
                // Remember the group info before resetting
                const groupInfo = {
                    site_id: inventoryForm.site_id,
                    period: inventoryForm.period,
                    inventory_type: inventoryForm.inventory_type
                };
                
                setEditingInventory(null);
                resetInventoryForm();
                await loadData();
                
                // Reopen the detail dialog with the updated group
                if (selectedInventoryGroup) {
                    setInventoryDetailDialog(true);
                }
            } catch (error) {
                toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
            }
        } else {
            // Multi-item create mode
            if (!inventoryForm.site_id || inventoryForm.items.length === 0) {
                toast.error('Site et au moins un produit sont requis');
                return;
            }
            
            try {
                // Create each inventory item
                for (const item of inventoryForm.items) {
                    const effectiveQuantity = calculateEffectiveQuantity(item);
                    const effectiveUnitPrice = calculateEffectiveUnitPrice(item);
                    const zoneQtys = calculateEffectiveZoneQuantities(item);
                    
                    await inventoriesAPI.create({
                        site_id: inventoryForm.site_id,
                        industrial_product_id: item.industrial_product_id,
                        supplier_product_id: item.supplier_product_id,
                        quantity: effectiveQuantity,
                        quantity_reserve: zoneQtys.quantity_reserve,
                        quantity_stand: zoneQtys.quantity_stand,
                        quantity_siege: zoneQtys.quantity_siege,
                        inventory_type: inventoryForm.inventory_type,
                        period: inventoryForm.period,
                        unit_price: effectiveUnitPrice
                    });
                }
                toast.success(`${inventoryForm.items.length} ligne(s) d'inventaire enregistrée(s)`);
                setInventoryDialog(false);
                resetInventoryForm();
                loadData();
            } catch (error) {
                toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
            }
        }
    };

    const resetInventoryForm = () => {
        setInventoryForm({
            site_id: '',
            inventory_type: 'initial',
            period: getCurrentPeriod(),
            items: []
        });
        setInventoryItem({ industrial_product_id: '', supplier_product_id: '', quantity: '' });
    };

    const handleOpenInventoryDialog = () => {
        // New flow: ask only for Site / Période / Type, then open the same
        // detail dialog as "Modifier" with an empty group.
        setNewInventoryForm({
            site_id: sites.length === 1 ? sites[0].id : '',
            period: getCurrentPeriod(),
            inventory_type: 'final',
        });
        setNewInventoryDialog(true);
    };

    // Build the merged list of items for an inventory group: existing
    // persisted rows + still-unpersisted seed rows from the received-base
    // endpoint. Used both when "opening" a new inventory from the form AND
    // when re-opening an existing group via the eye icon, so a partial save
    // never causes the user to lose their pre-populated product list.
    const buildInventoryGroupItems = async (site_id, period, inventory_type, existingItems) => {
        const persisted = (existingItems || []).map(inv => ({
            ...inv,
            product_name: inv.product_name || products.find(p => p.id === inv.industrial_product_id)?.name || '',
            supplier_product_name: inv.supplier_product_name || supplierProducts.find(s => s.id === inv.supplier_product_id)?.supplier_name_product || '',
        }));
        let seededExtras = [];
        try {
            const res = await inventoriesAPI.getReceivedBase(site_id, period);
            const receivedItems = res.data?.items || [];
            const persistedKey = new Set(persisted.map(i => (
                `${i.industrial_product_id}::${i.supplier_product_id || ''}`
            )));
            seededExtras = receivedItems
                .filter(it => !persistedKey.has(`${it.industrial_product_id}::${it.supplier_product_id || ''}`))
                .map(it => ({
                    id: null,
                    site_id,
                    period,
                    inventory_type,
                    industrial_product_id: it.industrial_product_id,
                    supplier_product_id: it.supplier_product_id || '',
                    product_name: it.product_name || '',
                    supplier_product_name: it.supplier_product_name || '',
                    product_unit: it.product_unit || 'pièce',
                    category: it.category || '',
                    unit_price: it.unit_price || 0,
                    capacity_value: it.capacity_value || null,
                    capacity_unit: it.capacity_unit || null,
                    purchase_unit: it.purchase_unit || '',
                    quantity: 0,
                    quantity_reserve: 0,
                    quantity_stand: 0,
                    quantity_siege: 0,
                    input_unit: 'base',
                }));
        } catch {
            // Non-blocking: if the endpoint fails, we still return the persisted rows.
        }
        return [...persisted, ...seededExtras];
    };

    const handleStartInventoryFromForm = async () => {
        const { site_id, period, inventory_type } = newInventoryForm;
        if (!site_id || !period || !inventory_type) {
            toast.error('Veuillez sélectionner site, période et type');
            return;
        }
        const existingItems = inventories.filter(i =>
            i.site_id === site_id && i.period === period && i.inventory_type === inventory_type
        );
        const site = sites.find(s => s.id === site_id);
        const items = await buildInventoryGroupItems(site_id, period, inventory_type, existingItems);
        const seedCount = items.length - existingItems.length;
        const group = {
            site_id,
            site_name: site?.name || '',
            period,
            inventory_type,
            items,
        };
        setSelectedInventoryGroup(group);
        setNewInventoryDialog(false);
        setInventoryDetailDialog(true);
        if (existingItems.length && seedCount > 0) {
            toast.info(`Inventaire chargé : ${existingItems.length} produit(s) déjà saisi(s) + ${seedCount} produit(s) réceptionné(s) restant(s) à saisir`);
        } else if (existingItems.length) {
            toast.info(`Inventaire existant chargé (${existingItems.length} produit(s))`);
        } else if (seedCount > 0) {
            toast.info(`Base initialisée avec ${seedCount} produit(s) déjà réceptionné(s) sur ce site`);
        }
    };

    const handleViewInventoryGroup = async (group) => {
        // Enrich the group with seed rows from received-base so the user
        // sees the full checklist of received products (not only those
        // already saved) even when reopening a partially-saved inventory.
        try {
            const items = await buildInventoryGroupItems(
                group.site_id,
                group.period,
                group.inventory_type,
                group.items || [],
            );
            setSelectedInventoryGroup({ ...group, items });
        } catch {
            setSelectedInventoryGroup(group);
        }
        setInventoryDetailDialog(true);
    };

    const closeMobileDetail = () => {
        setInventoryDetailDialog(false);
        setSelectedInventoryGroup(null);
    };

    // Legacy single-item edit
    const handleEditInventory = (inv) => {
        const product = products.find(p => p.id === inv.industrial_product_id);
        const sp = supplierProducts.find(s => s.id === inv.supplier_product_id);
        
        setEditingInventory(inv);
        setInventoryForm({
            site_id: inv.site_id,
            inventory_type: inv.inventory_type,
            period: inv.period,
            items: [{
                industrial_product_id: inv.industrial_product_id,
                supplier_product_id: inv.supplier_product_id || '',
                quantity: inv.quantity,
                quantity_reserve: inv.quantity_reserve || 0,
                quantity_stand: inv.quantity_stand || inv.quantity || 0, // Migration: existing quantities go to Stand
                quantity_siege: inv.quantity_siege || 0,
                product_name: inv.product_name,
                product_unit: (() => { const { baseUnit } = resolveInventoryUnits(sp, product); return baseUnit; })(),
                supplier_product_name: inv.supplier_product_name || '',
                unit_price: sp?.unit_price || inv.unit_price || 0,
                capacity_value: sp?.capacity_value || null,
                capacity_unit: sp?.capacity_unit || null,
                purchase_unit: (() => { const { purchaseUnitLabel } = resolveInventoryUnits(sp, product); return purchaseUnitLabel; })(),
                input_unit: 'base',
                category: getProductCategory(inv.industrial_product_id)
            }]
        });
        setInventoryDialog(true);
    };

    const handleDeleteInventoryClick = (inv) => {
        setInventoryToDelete(inv);
        setDeleteInventoryDialog(true);
    };

    // Add product directly from detail dialog
    const handleAddProductFromDetail = async () => {
        const totalQty = calculateZoneTotal(detailAddItem.quantity_reserve, detailAddItem.quantity_stand, detailAddItem.quantity_siege);
        
        if (!selectedInventoryGroup || !detailAddItem.industrial_product_id || totalQty <= 0) {
            toast.error('Sélectionnez un produit et saisissez au moins une quantité');
            return;
        }
        
        try {
            // Get the supplier product to get the unit_price and capacity info
            const sp = supplierProducts.find(s => s.id === detailAddItem.supplier_product_id);
            const product = products.find(p => p.id === detailAddItem.industrial_product_id);
            
            // Build a temporary item to calculate effective values
            const tempItem = {
                quantity: totalQty,
                quantity_reserve: parseFloat(detailAddItem.quantity_reserve) || 0,
                quantity_stand: parseFloat(detailAddItem.quantity_stand) || 0,
                quantity_siege: parseFloat(detailAddItem.quantity_siege) || 0,
                unit_price: sp?.unit_price || 0,
                input_unit: detailAddItem.input_unit,
                capacity_value: sp?.capacity_value || null
            };
            
            const effectiveQuantity = calculateEffectiveQuantity(tempItem);
            const effectiveUnitPrice = calculateEffectiveUnitPrice(tempItem);
            const zoneQtys = calculateEffectiveZoneQuantities(tempItem);
            
            await inventoriesAPI.create({
                site_id: selectedInventoryGroup.site_id,
                industrial_product_id: detailAddItem.industrial_product_id,
                supplier_product_id: detailAddItem.supplier_product_id || '',
                quantity: effectiveQuantity,
                quantity_reserve: zoneQtys.quantity_reserve,
                quantity_stand: zoneQtys.quantity_stand,
                quantity_siege: zoneQtys.quantity_siege,
                inventory_type: selectedInventoryGroup.inventory_type,
                period: selectedInventoryGroup.period,
                unit_price: effectiveUnitPrice
            });
            toast.success('Produit ajouté à l\'inventaire');
            setDetailAddItem({ industrial_product_id: '', supplier_product_id: '', quantity_reserve: '', quantity_stand: '', quantity_siege: '', input_unit: 'base' });
            
            // Reload data and update selected group
            await loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
        }
    };

    const handleSaveReception = async () => {
        if (!receptionForm.site_id || !receptionForm.industrial_product_id || !receptionForm.quantity) {
            toast.error('Site, produit et quantité sont requis');
            return;
        }
        
        try {
            await stockMovementsAPI.create({
                ...receptionForm,
                quantity: parseFloat(receptionForm.quantity),
                movement_type: 'reception'
            });
            toast.success('Réception enregistrée');
            setReceptionDialog(false);
            setReceptionForm({
                site_id: '',
                industrial_product_id: '',
                supplier_product_id: '',
                quantity: '',
                date: new Date().toISOString().split('T')[0],
                notes: ''
            });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        }
    };

    const handleDeleteMovement = async () => {
        try {
            await stockMovementsAPI.delete(movementToDelete.id);
            toast.success('Mouvement supprimé');
            setDeleteDialog(false);
            loadData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleConfirmDeleteInventory = async () => {
        if (!inventoryToDelete) return;
        try {
            await inventoriesAPI.delete(inventoryToDelete.id);
            toast.success('Inventaire supprimé');
            setDeleteInventoryDialog(false);
            setInventoryToDelete(null);
            // Close detail dialog if open
            if (inventoryDetailDialog) {
                // Refresh the selected group
                const updatedGroup = inventoryGroups.find(g => 
                    g.site_id === selectedInventoryGroup?.site_id && 
                    g.period === selectedInventoryGroup?.period && 
                    g.inventory_type === selectedInventoryGroup?.inventory_type
                );
                if (updatedGroup) {
                    setSelectedInventoryGroup(updatedGroup);
                } else {
                    setInventoryDetailDialog(false);
                }
            }
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    // Delete entire inventory group (first confirmation)
    const handleDeleteGroupClick = (group) => {
        setGroupToDelete(group);
        setDeleteGroupDialog(true);
    };

    // Second confirmation for group deletion
    const handleFirstConfirmDeleteGroup = () => {
        setDeleteGroupDialog(false);
        setDeleteConfirmText('');
        setDeleteGroupConfirmDialog(true);
    };

    // Final deletion of group
    const handleConfirmDeleteGroup = async () => {
        if (!groupToDelete || deleteConfirmText !== 'SUPPRIMER') return;
        
        try {
            await inventoriesAPI.deleteGroup(
                groupToDelete.site_id,
                groupToDelete.period,
                groupToDelete.inventory_type
            );
            toast.success(`Inventaire supprimé (${groupToDelete.total_items} produits)`);
            setDeleteGroupConfirmDialog(false);
            setGroupToDelete(null);
            setDeleteConfirmText('');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const handleExport = () => {
        const params = {};
        if (filterSite !== 'all') params.site_id = filterSite;
        params.period = filterPeriod;
        downloadAuthFile(exportAPI.inventory(params), 'inventaire.xlsx');
    };

    const handleDownloadInventoryFormPdf = () => {
        if (!pdfFormSiteId) {
            toast.error('Veuillez sélectionner un site');
            return;
        }
        const url = sitesAPI.getInventoryFormPdfUrl(pdfFormSiteId, pdfFormPeriod);
        openAuthPdf(url, 'Erreur lors de la génération du PDF');
        setInventoryFormPdfDialog(false);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
            </div>
        );
    }


    // Shared logic to prepare sorted/grouped inventory data
    // Group inventory items by category for the detail dialog.
    // Thin wrapper over the pure helper that captures `selectedInventoryGroup` + `products`.
    const getGroupedInventoryData = () => getGroupedInventoryDataPure(selectedInventoryGroup, products);

    // Display-time data for a persisted inventory line.
    // Thin wrapper over the pure helper that captures supplierProducts/products from the closure.
    const getInvItemData = (inv) => getInvItemDataPure(inv, supplierProducts, products);

    // Shared save helper — creates the inventory line the first time it is
    // touched (when it originates from the "seeded from receptions" flow, in
    // which case `inv.id` is null) or updates it otherwise. Matches by
    // industrial_product_id + supplier_product_id so we can patch the local
    // state even when the id was just generated by the backend.
    const saveInventoryLine = async (inv, payload) => {
        const isNew = !inv.id;
        const res = isNew
            ? await inventoriesAPI.create(payload)
            : await inventoriesAPI.update(inv.id, payload);
        const saved = res?.data || {};
        const newId = saved.id || inv.id;
        const matches = (i) => (
            inv.id ? i.id === inv.id
                : (
                    !i.id
                    && i.industrial_product_id === inv.industrial_product_id
                    && (i.supplier_product_id || '') === (inv.supplier_product_id || '')
                )
        );
        setInventories((prev) => {
            const already = prev.some(i => i.id === newId);
            if (isNew && !already) return [...prev, { ...inv, ...saved, id: newId }];
            return prev.map(i => matches(i) ? { ...i, ...saved, id: newId } : i);
        });
        setSelectedInventoryGroup((prev) => prev ? {
            ...prev,
            items: prev.items.map(i => matches(i) ? { ...i, ...saved, id: newId } : i),
        } : prev);
        return { ...saved, id: newId };
    };

    // Desktop table rows
    const renderInventoryRows = () => {
        const groupedByCategory = getGroupedInventoryData();
        const rows = [];

        // Toggle unit_mode for a given inventory line (purchase ↔ base).
        // IMPORTANT: We deliberately do NOT re-scale the stored quantities on
        // toggle. If the user typed "5" while in Paquet mode then switches to
        // pièce mode, the display must keep showing "5" (now interpreted as
        // 5 pièces). To achieve that, we invert the display formula's
        // divisor by adjusting the stored value in sync:
        //   - purchase → base: stored /= capacity_value
        //   - base → purchase: stored *= capacity_value
        // This preserves the number the user typed and lets them correct the
        // unit without triggering an unwanted scale change.
        const handleToggleUnitMode = async (inv, newMode) => {
            const currentMode = inv.unit_mode || 'purchase';
            if (currentMode === newMode) return;
            const sp = supplierProducts.find(s => s.id === inv.supplier_product_id);
            const cap = (sp?.capacity_value || 1);
            // Ratio to apply so display stays constant across the toggle.
            let ratio = 1;
            if (currentMode === 'purchase' && newMode === 'base') {
                ratio = cap > 0 ? 1 / cap : 1;
            } else if (currentMode === 'base' && newMode === 'purchase') {
                ratio = cap;
            }
            const newQR = (inv.quantity_reserve || 0) * ratio;
            const newQS = (inv.quantity_stand || 0) * ratio;
            const newQSg = (inv.quantity_siege || 0) * ratio;
            const newQty = newQR + newQS + newQSg;
            try {
                await saveInventoryLine(inv, {
                    site_id: inv.site_id,
                    period: inv.period,
                    inventory_type: inv.inventory_type,
                    industrial_product_id: inv.industrial_product_id,
                    supplier_product_id: inv.supplier_product_id || '',
                    quantity: newQty,
                    quantity_reserve: newQR,
                    quantity_stand: newQS,
                    quantity_siege: newQSg,
                    unit_price: inv.unit_price,
                    unit_mode: newMode,
                });
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Erreur de mise a jour');
            }
        };

        // Inline edit handler: parses value, recomputes total, saves to backend, updates local state.
        // The displayed value depends on inv.unit_mode:
        //  - 'purchase' (default): user enters in purchase-units (Bidon/Sachet/Carton...)
        //                           we multiply by capacity_value to get base units to store.
        //  - 'base': user enters directly in base unit (kg/L/pcs), stored as-is.
        const handleQuickUpdate = async (inv, field, rawValue) => {
            const parsed = rawValue === '' || rawValue === null || rawValue === undefined
                ? 0
                : parseFloat(String(rawValue).replace(',', '.'));
            if (Number.isNaN(parsed) || parsed < 0) return;
            const sp = supplierProducts.find(s => s.id === inv.supplier_product_id);
            const capacityValue = sp?.capacity_value || 1;
            const mode = inv.unit_mode || 'purchase';
            // Convert displayed value to base unit
            const baseValue = mode === 'purchase' ? parsed * capacityValue : parsed;
            if ((inv[field] || 0) === baseValue) return;
            const newReserve = field === 'quantity_reserve' ? baseValue : (inv.quantity_reserve || 0);
            const newStand = field === 'quantity_stand' ? baseValue : (inv.quantity_stand || 0);
            const newSiege = field === 'quantity_siege' ? baseValue : (inv.quantity_siege || 0);
            const newTotal = (newReserve || 0) + (newStand || 0) + (newSiege || 0);
            try {
                await saveInventoryLine(inv, {
                    site_id: inv.site_id,
                    period: inv.period,
                    inventory_type: inv.inventory_type,
                    industrial_product_id: inv.industrial_product_id,
                    supplier_product_id: inv.supplier_product_id || '',
                    quantity: newTotal,
                    quantity_reserve: newReserve,
                    quantity_stand: newStand,
                    quantity_siege: newSiege,
                    unit_price: inv.unit_price,
                    unit_mode: mode,
                });
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Erreur de mise a jour');
            }
        };

        // Inline editable cell: extracted to /components/inventory/QtyCell.jsx.
        // Local wrapper captures supplierProducts/canEdit/handleQuickUpdate from the closure.
        const InlineQtyCell = ({ inv, field, value, align = 'text-right' }) => {
            const sp = supplierProducts.find(s => s.id === inv.supplier_product_id);
            const capacityValue = sp?.capacity_value || 1;
            const mode = inv.unit_mode || 'purchase';
            const variableWeight = !!sp?.variable_weight;
            return (
                <QtyCell
                    inv={inv}
                    field={field}
                    value={value}
                    capacityValue={capacityValue}
                    mode={mode}
                    canEdit={canEdit}
                    onCommit={handleQuickUpdate}
                    align={align}
                    variableWeight={variableWeight}
                />
            );
        };

        // Unit toggle for a line: [Bidon | L] - stored in unit_mode
        // UnitToggle component: extracted to /components/inventory/UnitToggle.jsx.
        // Local alias keeps the JSX call site identical.
        const InlineUnitToggle = ({ inv }) => (
            <UnitToggle inv={inv} getInvItemData={getInvItemData} onToggle={handleToggleUnitMode} canEdit={canEdit} />
        );

        Object.entries(groupedByCategory).forEach(([category, items]) => {
            let categoryTotal = 0;
            rows.push(
                <tr key={`header-${category}`} className="bg-slate-50">
                    <td colSpan="8" className="p-2 pl-3"><span className="text-sm font-semibold text-slate-600">{category}</span></td>
                </tr>
            );
            const byProduct = {};
            items.forEach(inv => { const pid = inv.industrial_product_id; if (!byProduct[pid]) byProduct[pid] = []; byProduct[pid].push(inv); });
            Object.values(byProduct).forEach(productItems => {
                productItems.forEach((inv, idx) => {
                    const { sp, baseUnit, lineTotal, qtyReserve, qtyStand, qtySiege } = getInvItemData(inv);
                    const isFirst = idx === 0;
                    categoryTotal += lineTotal;
                    // Seeded rows (id == null) need a composite React key,
                    // otherwise every seed row shares key=null and React
                    // collapses them into a single DOM node — the second edit
                    // then upserts the same DB record as the first.
                    const rowKey = inv.id || `seed-${inv.industrial_product_id}-${inv.supplier_product_id || 'none'}`;
                    rows.push(
                        <tr key={rowKey} className={`${isFirst ? 'border-t' : ''} hover:bg-slate-50`} style={!isFirst ? { lineHeight: '1.2' } : undefined}>
                            <td className={`${isFirst ? 'pt-1 pb-1 px-3 align-bottom' : 'py-0 px-3'} pl-4`}>
                                {isFirst && <div className="font-semibold text-sm text-slate-700 mb-0.5">{inv.product_name}</div>}
                                <div className="text-sm text-slate-400 pl-4 leading-tight">{inv.supplier_product_name || '-'}</div>
                            </td>
                            <td className={`${isFirst ? 'pt-2 pb-0.5 px-3 align-bottom' : 'py-0 px-3'} text-slate-500 text-sm`}>{sp?.supplier_name || '-'}</td>
                            <td className={`${isFirst ? 'pt-2 pb-0.5 px-3 align-bottom' : 'py-0 px-3'} text-right text-sm`}>
                                <InlineQtyCell inv={inv} field="quantity_reserve" value={qtyReserve} />
                            </td>
                            <td className={`${isFirst ? 'pt-2 pb-0.5 px-3 align-bottom' : 'py-0 px-3'} text-right text-sm`}>
                                <InlineQtyCell inv={inv} field="quantity_stand" value={qtyStand} />
                            </td>
                            <td className={`${isFirst ? 'pt-2 pb-0.5 px-3 align-bottom' : 'py-0 px-3'} text-right text-sm`}>
                                <InlineQtyCell inv={inv} field="quantity_siege" value={qtySiege} />
                            </td>
                            <td className={`${isFirst ? 'pt-2 pb-0.5 px-3 align-bottom' : 'py-0 px-3'} text-right font-semibold bg-slate-50`}>
                                <div className="flex items-center justify-end gap-2">
                                    <span className="tabular-nums text-right">
                                        {(() => {
                                            const mode = inv.unit_mode || 'purchase';
                                            const cap = (sp?.capacity_value || 1);
                                            const dispTotal = mode === 'purchase' && cap > 0 ? (inv.quantity / cap) : inv.quantity;
                                            return Number.isInteger(dispTotal) ? dispTotal : dispTotal.toFixed(2).replace(/\.?0+$/, '');
                                        })()}
                                    </span>
                                    <div className="w-[7rem] flex justify-start flex-shrink-0">
                                        <InlineUnitToggle inv={inv} />
                                    </div>
                                </div>
                            </td>
                            <td className={`${isFirst ? 'pt-2 pb-0.5 px-3 align-bottom' : 'py-0 px-3'} text-right font-semibold text-emerald-600`}>{inv.unit_price ? formatCurrency(lineTotal) : '-'}</td>
                            <td className={`${isFirst ? 'pt-2 pb-0.5 px-3 align-bottom' : 'py-0 px-3'} text-center`}>
                                <div className="flex items-center justify-center gap-1">
                                    <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700" onClick={() => { setInventoryDetailDialog(false); setTimeout(() => handleEditInventory(inv), 150); }} title="Modifier en détail (prix unitaire, fournisseur...)" disabled={!canEdit}><Pencil className="w-4 h-4" /></Button>
                                    {canEdit && <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteInventoryClick(inv)} title="Supprimer"><Trash2 className="w-4 h-4" /></Button>}
                                </div>
                            </td>
                        </tr>
                    );
                });
            });
            rows.push(
                <tr key={`subtotal-${category}`} className="border-t bg-emerald-50">
                    <td colSpan="6" className="p-2 pr-3 text-right text-sm text-slate-600">Sous-total {category} :</td>
                    <td className="p-2 text-right font-semibold text-emerald-700">{formatCurrency(categoryTotal)}</td>
                    <td></td>
                </tr>
            );
        });
        return rows;
    };

    // Mobile card layout
    const renderInventoryCards = () => {
        const groupedByCategory = getGroupedInventoryData();
        const cards = [];

        const handleToggleUnitMode = async (inv, newMode) => {
            const currentMode = inv.unit_mode || 'purchase';
            if (currentMode === newMode) return;
            const sp = supplierProducts.find(s => s.id === inv.supplier_product_id);
            const cap = (sp?.capacity_value || 1);
            let ratio = 1;
            if (currentMode === 'purchase' && newMode === 'base') {
                ratio = cap > 0 ? 1 / cap : 1;
            } else if (currentMode === 'base' && newMode === 'purchase') {
                ratio = cap;
            }
            const newQR = (inv.quantity_reserve || 0) * ratio;
            const newQS = (inv.quantity_stand || 0) * ratio;
            const newQSg = (inv.quantity_siege || 0) * ratio;
            const newQty = newQR + newQS + newQSg;
            try {
                await saveInventoryLine(inv, {
                    site_id: inv.site_id,
                    period: inv.period,
                    inventory_type: inv.inventory_type,
                    industrial_product_id: inv.industrial_product_id,
                    supplier_product_id: inv.supplier_product_id || '',
                    quantity: newQty,
                    quantity_reserve: newQR,
                    quantity_stand: newQS,
                    quantity_siege: newQSg,
                    unit_price: inv.unit_price,
                    unit_mode: newMode,
                });
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Erreur de mise a jour');
            }
        };

        const handleQuickUpdate = async (inv, field, rawValue) => {
            const parsed = rawValue === '' || rawValue === null || rawValue === undefined
                ? 0
                : parseFloat(String(rawValue).replace(',', '.'));
            if (Number.isNaN(parsed) || parsed < 0) return;
            const sp = supplierProducts.find(s => s.id === inv.supplier_product_id);
            const capacityValue = sp?.capacity_value || 1;
            const mode = inv.unit_mode || 'purchase';
            const baseValue = mode === 'purchase' ? parsed * capacityValue : parsed;
            if ((inv[field] || 0) === baseValue) return;
            const newReserve = field === 'quantity_reserve' ? baseValue : (inv.quantity_reserve || 0);
            const newStand = field === 'quantity_stand' ? baseValue : (inv.quantity_stand || 0);
            const newSiege = field === 'quantity_siege' ? baseValue : (inv.quantity_siege || 0);
            const newTotal = (newReserve || 0) + (newStand || 0) + (newSiege || 0);
            try {
                await saveInventoryLine(inv, {
                    site_id: inv.site_id,
                    period: inv.period,
                    inventory_type: inv.inventory_type,
                    industrial_product_id: inv.industrial_product_id,
                    supplier_product_id: inv.supplier_product_id || '',
                    quantity: newTotal,
                    quantity_reserve: newReserve,
                    quantity_stand: newStand,
                    quantity_siege: newSiege,
                    unit_price: inv.unit_price,
                    unit_mode: mode,
                });
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Erreur de mise a jour');
            }
        };

        // MobileQtyCell: extracted to /components/inventory/QtyCell.jsx.
        const InlineMobileQtyCell = ({ inv, field, value, label }) => {
            const sp = supplierProducts.find(s => s.id === inv.supplier_product_id);
            const capacityValue = sp?.capacity_value || 1;
            const mode = inv.unit_mode || 'purchase';
            const variableWeight = !!sp?.variable_weight;
            return (
                <MobileQtyCell
                    inv={inv}
                    field={field}
                    value={value}
                    capacityValue={capacityValue}
                    mode={mode}
                    canEdit={canEdit}
                    onCommit={handleQuickUpdate}
                    label={label}
                    variableWeight={variableWeight}
                />
            );
        };

        // MobileUnitToggle component: extracted to /components/inventory/UnitToggle.jsx.
        // Local alias keeps the JSX call site identical.
        const InlineMobileUnitToggle = ({ inv }) => (
            <MobileUnitToggle inv={inv} getInvItemData={getInvItemData} onToggle={handleToggleUnitMode} canEdit={canEdit} />
        );

        Object.entries(groupedByCategory).forEach(([category, items]) => {
            let categoryTotal = 0;
            cards.push(
                <div key={`cat-${category}`} className="bg-slate-100 px-3 py-2 rounded-t-lg mt-2 first:mt-0">
                    <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">{category}</span>
                </div>
            );
            const byProduct = {};
            items.forEach(inv => { const pid = inv.industrial_product_id; if (!byProduct[pid]) byProduct[pid] = []; byProduct[pid].push(inv); });
            Object.values(byProduct).forEach(productItems => {
                productItems.forEach((inv) => {
                    const { baseUnit, lineTotal, qtyReserve, qtyStand, qtySiege } = getInvItemData(inv);
                    categoryTotal += lineTotal;
                    const cardKey = inv.id || `seed-${inv.industrial_product_id}-${inv.supplier_product_id || 'none'}`;
                    cards.push(
                        <div key={cardKey} className="border-b border-slate-100 px-2 py-1.5 bg-white">
                            <div className="flex items-center justify-between gap-1">
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm text-slate-800 truncate">{inv.product_name}</div>
                                    {inv.supplier_product_name && <div className="text-xs text-slate-400 truncate">{inv.supplier_product_name}</div>}
                                </div>
                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                    {(() => {
                                        const sp2 = supplierProducts.find(s => s.id === inv.supplier_product_id);
                                        const cap = sp2?.capacity_value || 1;
                                        const mode = inv.unit_mode || 'purchase';
                                        const dispTotal = mode === 'purchase' && cap > 0 ? (inv.quantity / cap) : inv.quantity;
                                        const txt = Number.isInteger(dispTotal) ? String(dispTotal) : dispTotal.toFixed(2).replace(/\.?0+$/, '');
                                        return <span className="text-xs font-semibold text-slate-800 whitespace-nowrap">{txt}</span>;
                                    })()}
                                    <InlineMobileUnitToggle inv={inv} />
                                    {inv.unit_price > 0 && <span className="text-xs font-semibold text-emerald-600 whitespace-nowrap ml-1">{formatCurrency(lineTotal)}</span>}
                                    <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500" onClick={() => { setInventoryDetailDialog(false); setTimeout(() => handleEditInventory(inv), 150); }} disabled={!canEdit}><Pencil className="w-3 h-3" /></Button>
                                    {canEdit && <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleDeleteInventoryClick(inv)}><Trash2 className="w-3 h-3" /></Button>}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mt-1 pl-1 flex-wrap">
                                <InlineMobileQtyCell inv={inv} field="quantity_reserve" value={qtyReserve} label="Rés." />
                                <InlineMobileQtyCell inv={inv} field="quantity_stand" value={qtyStand} label="Std." />
                                <InlineMobileQtyCell inv={inv} field="quantity_siege" value={qtySiege} label="Siè." />
                            </div>
                        </div>
                    );
                });
            });
            cards.push(
                <div key={`sub-${category}`} className="bg-emerald-50 px-3 py-1.5 flex justify-between text-xs rounded-b-lg mb-1">
                    <span className="text-slate-600">Sous-total {category}</span>
                    <span className="font-bold text-emerald-700">{formatCurrency(categoryTotal)}</span>
                </div>
            );
        });
        return cards;
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="inventory-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Stock</h1>
                    <p className="text-slate-500 mt-1">Inventaires, mouvements et consommation</p>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                    <Button variant="outline" onClick={() => setInventoryFormPdfDialog(true)} data-testid="inventory-form-pdf-btn">
                        <FileText className="w-4 h-4 mr-2" />
                        Fiche inventaire PDF
                    </Button>
                    <Button variant="outline" onClick={handleExport} data-testid="export-inventory-btn">
                        <Download className="w-4 h-4 mr-2" />
                        Export Excel
                    </Button>
                    <Button variant="outline" onClick={() => setReceptionDialog(true)} data-testid="add-reception-btn">
                        <PackagePlus className="w-4 h-4 mr-2" />
                        Réception
                    </Button>
                    <Button onClick={handleOpenInventoryDialog} className="btn-primary" data-testid="add-inventory-btn">
                        <ClipboardList className="w-4 h-4 mr-2" />
                        Saisie inventaire
                    </Button>
                </div>
            </div>

            {/* Shortcut bar (Entreprise / Sites FE / Fournisseurs) */}
            <div className="flex flex-col gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg" data-testid="inventory-shortcuts">
                {/* Ligne 1 : entreprise */}
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500 mr-1">Entreprise :</span>
                    {['all', ...Object.keys(COMPANY_MATCHERS)].map((company) => {
                        const isActive = filterCompany === company;
                        const label = company === 'all' ? 'Toutes' : company;
                        return (
                            <button
                                key={company}
                                type="button"
                                onClick={() => {
                                    if (filterSite !== 'all') setFilterSite('all');
                                    setFilterCompany(company);
                                }}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                                    isActive
                                        ? 'bg-slate-900 text-white border-slate-900'
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                }`}
                                data-testid={`inventory-company-shortcut-${company}`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
                {/* Ligne 2 : sites FE */}
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500 mr-1 flex-shrink-0">Sites FE :</span>
                    {['Génipa', 'Cluny', 'Dillon', 'Océanis', 'Vauclin', 'Siège'].map((shortName) => {
                        const fullName = `FE ${shortName}`;
                        const site = sites.find(s => s.name === fullName);
                        const isActive = site && filterSite === site.id;
                        return (
                            <button
                                key={shortName}
                                type="button"
                                disabled={!site}
                                onClick={() => {
                                    if (filterCompany !== 'all') setFilterCompany('all');
                                    setFilterSite(isActive ? 'all' : site.id);
                                }}
                                className={`lg:flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border text-center ${
                                    !site
                                        ? 'bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed'
                                        : isActive
                                            ? 'bg-slate-900 text-white border-slate-900'
                                            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                }`}
                                data-testid={`inventory-site-shortcut-${shortName.toLowerCase()}`}
                            >
                                {shortName}
                            </button>
                        );
                    })}
                </div>
                {/* Ligne 3 : fournisseurs */}
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500 mr-1 flex-shrink-0">Fournisseurs :</span>
                    {supplierShortcutNames.map((name, idx) => {
                        const supplier = resolveShortcutSupplier(name);
                        const isActive = supplier && filterSupplier === supplier.id;
                        return (
                            <button
                                key={`${name}-${idx}`}
                                type="button"
                                disabled={!supplier}
                                onClick={() => supplier && setFilterSupplier(isActive ? 'all' : supplier.id)}
                                className={`lg:flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border text-center ${
                                    !supplier
                                        ? 'bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed'
                                        : isActive
                                            ? 'bg-emerald-600 text-white border-emerald-600'
                                            : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'
                                }`}
                                title={!supplier ? `Fournisseur introuvable : ${name}` : supplier.name}
                                data-testid={`inventory-supplier-shortcut-${normalizeStr(name)}`}
                            >
                                {supplierShortcutLabel(supplier?.name || name)}
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        onClick={() => setShowShortcutEditor(true)}
                        className="px-2.5 py-1.5 rounded-full text-xs font-semibold text-slate-500 border border-dashed border-slate-300 hover:border-emerald-400 hover:text-emerald-600 flex-shrink-0"
                        data-testid="inventory-edit-supplier-shortcuts-btn"
                    >
                        Modifier
                    </button>
                </div>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <Select value={filterSite} onValueChange={setFilterSite}>
                            <SelectTrigger className="w-auto min-w-[120px] h-8 text-xs" data-testid="filter-site-select">
                                <SelectValue placeholder="Site" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all" className="py-1.5 text-xs leading-snug">Tous les sites</SelectItem>
                                {sites.map(s => (
                                    <SelectItem key={s.id} value={s.id} className="py-1.5 text-xs leading-snug">{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterCategory} onValueChange={setFilterCategory}>
                            <SelectTrigger className="w-auto min-w-[130px] h-8 text-xs" data-testid="filter-category-select">
                                <SelectValue placeholder="Catégorie" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all" className="py-1.5 text-xs leading-snug">Toutes catégories</SelectItem>
                                {categories.map(cat => (
                                    <SelectItem key={cat} value={cat} className="py-1.5 text-xs leading-snug">{cat}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterFamily} onValueChange={setFilterFamily}>
                            <SelectTrigger className="w-auto min-w-[120px] h-8 text-xs" data-testid="filter-family-select">
                                <SelectValue placeholder="Famille" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all" className="py-1.5 text-xs leading-snug">Toutes familles</SelectItem>
                                {families.map(f => (
                                    <SelectItem key={f} value={f} className="py-1.5 text-xs leading-snug">{f}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterProduct} onValueChange={setFilterProduct}>
                            <SelectTrigger className="w-auto min-w-[140px] h-8 text-xs" data-testid="filter-product-select">
                                <SelectValue placeholder="Produit" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all" className="py-1.5 text-xs leading-snug">Tous les produits</SelectItem>
                                {(() => {
                                    // Only show products present in movements (filtered by site, period, category, family)
                                    const movProductIds = new Set(movements.filter(mov => {
                                        if (!isSiteAllowed(mov.site_id)) return false;
                                        const matchesSite = filterSite === 'all' || mov.site_id === filterSite;
                                        const matchesPeriod = mov.date.startsWith(filterPeriod);
                                        const productCategory = getProductCategory(mov.industrial_product_id);
                                        const matchesCategory = filterCategory === 'all' || productCategory === filterCategory;
                                        const productFamily = getProductFamily(mov.industrial_product_id);
                                        const matchesFamily = filterFamily === 'all' || productFamily === filterFamily;
                                        return matchesSite && matchesPeriod && matchesCategory && matchesFamily;
                                    }).map(m => m.industrial_product_id));
                                    const sorted = products.filter(p => movProductIds.has(p.id)).sort((a, b) => {
                                        const catA = a.category || 'Sans catégorie';
                                        const catB = b.category || 'Sans catégorie';
                                        if (catA !== catB) return catA.localeCompare(catB, 'fr');
                                        return a.name.localeCompare(b.name, 'fr');
                                    });
                                    let lastCat = null;
                                    return sorted.map(p => {
                                        const cat = p.category || 'Sans catégorie';
                                        const showHeader = cat !== lastCat;
                                        lastCat = cat;
                                        return (
                                            <React.Fragment key={p.id}>
                                                {showHeader && (
                                                    <div className="px-2 py-0 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50">{cat}</div>
                                                )}
                                                <SelectItem value={p.id} className="pl-5 py-1.5 text-xs leading-snug">{p.name}</SelectItem>
                                            </React.Fragment>
                                        );
                                    });
                                })()}
                            </SelectContent>
                        </Select>
                        <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                            <SelectTrigger className="w-auto min-w-[120px] h-8 text-xs" data-testid="filter-period-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {periodOptions.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value} className="py-1.5 text-xs leading-snug">{opt.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Mobile Inline Detail View - Portal-like overlay */}
            {/* Tabs */}
            <div>
            <Tabs defaultValue="inventory" className="space-y-4">
                <TabsList className="flex flex-wrap h-auto gap-1 justify-start w-full sm:w-auto sm:inline-flex sm:h-9">
                    <TabsTrigger value="inventory" data-testid="inventory-tab">
                        <ClipboardList className="w-4 h-4 mr-2" />
                        Inventaires ({inventoryGroups.length})
                    </TabsTrigger>
                    <TabsTrigger value="movements" data-testid="movements-tab">
                        <PackagePlus className="w-4 h-4 mr-2" />
                        Mouvements ({filteredMovements.length})
                    </TabsTrigger>
                    <TabsTrigger value="consumption" data-testid="consumption-tab">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Consommation
                    </TabsTrigger>
                    {canManageInventoryForms && (
                        <TabsTrigger value="inventory-forms" data-testid="inventory-forms-tab">
                            <FileText className="w-4 h-4 mr-2" />
                            Fiches inventaire
                        </TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="inventory">
                    <Card>
                        <CardContent className="p-0">
                            {inventoryGroups.length === 0 ? (
                                <div className="py-12 text-center">
                                    <Warehouse className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                                    <p className="text-slate-500">Aucun inventaire pour cette période</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full data-table">
                                        <thead>
                                            <tr className="table-header">
                                                <th className="text-left p-4">Période</th>
                                                <th className="text-left p-4">Site</th>
                                                <th className="text-center p-4">Type</th>
                                                <th className="text-right p-4">Nb produits</th>
                                                <th className="text-center p-4">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {inventoryGroups.map((group) => (
                                                <tr key={`${group.site_id}-${group.period}-${group.inventory_type}`} className="table-row hover:bg-slate-50 cursor-pointer" onClick={() => handleViewInventoryGroup(group)}>
                                                    <td className="p-4 font-medium">{group.period}</td>
                                                    <td className="p-4">{group.site_name}</td>
                                                    <td className="p-4 text-center">
                                                        <Badge variant="outline">{getInventoryTypeLabel(group.inventory_type)}</Badge>
                                                    </td>
                                                    <td className="p-4 text-right font-semibold">{group.total_items}</td>
                                                    <td className="p-4 text-center">
                                                        <div className="flex items-center justify-center gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={(e) => { e.stopPropagation(); handleViewInventoryGroup(group); }}
                                                                data-testid={`view-inventory-${group.site_id}-${group.period}`}
                                                                title="Voir détails"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </Button>
                                                            {canDelete && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={(e) => { e.stopPropagation(); handleDeleteGroupClick(group); }}
                                                                data-testid={`delete-inventory-group-${group.site_id}-${group.period}`}
                                                                title="Supprimer l'inventaire"
                                                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="movements">
                    <Card>
                        <CardContent className="p-0">
                            {filteredMovements.length === 0 ? (
                                <div className="py-12 text-center">
                                    <PackagePlus className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                                    <p className="text-slate-500">Aucun mouvement pour cette période</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full data-table">
                                        <thead>
                                            <tr className="table-header">
                                                <th className="text-left px-3 py-1 whitespace-nowrap">Date</th>
                                                <th className="text-left px-3 py-1 whitespace-nowrap">Site</th>
                                                <th className="text-left px-3 py-1">Produit</th>
                                                <th className="text-center px-3 py-1 whitespace-nowrap">Type</th>
                                                <th className="text-right px-3 py-1 whitespace-nowrap">Quantité</th>
                                                <th className="text-right px-3 py-1 whitespace-nowrap">Tarif</th>
                                                <th className="text-left px-3 py-1">Notes</th>
                                                <th className="text-right px-1 py-1"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredMovements.map((mov) => (
                                                <tr key={mov.id} className="table-row">
                                                    <td className="px-3 py-1 whitespace-nowrap">{formatDate(mov.date)}</td>
                                                    <td className="px-3 py-1 whitespace-nowrap">{mov.site_name}</td>
                                                    <td className="px-3 py-1 min-w-[180px]">
                                                        <div className="font-semibold text-sm text-slate-700">{mov.product_name}</div>
                                                        {mov.supplier_product_name && (
                                                            <div className="text-sm text-slate-400 pl-4 leading-tight break-words">{mov.supplier_product_name}</div>
                                                        )}
                                                        {mov.supplier_name && (
                                                            <div className="text-xs text-slate-400 pl-4 italic break-words">{mov.supplier_name}</div>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-1 text-center whitespace-nowrap">
                                                        <Badge className={mov.movement_type === 'transfer_out' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>
                                                            {getMovementTypeLabel(mov.movement_type)}
                                                        </Badge>
                                                    </td>
                                                    <td className={`px-3 py-1 text-right font-semibold whitespace-nowrap ${mov.movement_type === 'transfer_out' ? 'text-red-600' : 'text-emerald-600'}`}>
                                                        {mov.movement_type === 'transfer_out' ? '-' : '+'}{mov.quantity}
                                                    </td>
                                                    <td className="px-3 py-1 text-right whitespace-nowrap">{mov.unit_price ? `${mov.unit_price.toFixed(2)} €` : '-'}</td>
                                                    <td className="px-3 py-1 text-slate-500 text-sm max-w-[200px] truncate">{mov.source_label || mov.notes || '-'}</td>
                                                    <td className="px-1 py-1 text-right">
                                                        {canEdit && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                                            onClick={() => { setMovementToDelete(mov); setDeleteDialog(true); }}
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="consumption">
                    {(() => {
                        // 1) Specific site → use it (single-site mode)
                        // 2) Company alone → aggregate across all sites of that company
                        // 3) Neither → aggregate across ALL allowed sites
                        if (filterSite !== 'all') {
                            const activeSite = sites.find(s => s.id === filterSite);
                            return (
                                <ConsumptionTab
                                    siteId={filterSite}
                                    siteName={activeSite?.name || ''}
                                    period={filterPeriod}
                                />
                            );
                        }
                        if (filterCompany !== 'all') {
                            const matcher = COMPANY_MATCHERS[filterCompany];
                            const companySites = sites.filter(s => matcher && matcher(s.name));
                            if (companySites.length === 0) {
                                return (
                                    <Card>
                                        <CardContent className="py-10 text-center text-slate-500 text-sm">
                                            Aucun site « {filterCompany} » disponible.
                                        </CardContent>
                                    </Card>
                                );
                            }
                            return (
                                <ConsumptionTab
                                    siteIds={companySites.map(s => s.id)}
                                    siteName={filterCompany}
                                    period={filterPeriod}
                                />
                            );
                        }
                        if (sites.length === 0) return null;
                        return (
                            <ConsumptionTab
                                siteIds={sites.map(s => s.id)}
                                siteName="Tous les sites"
                                period={filterPeriod}
                            />
                        );
                    })()}
                </TabsContent>

                {canManageInventoryForms && (
                    <TabsContent value="inventory-forms">
                        <InventoryFormsPage />
                    </TabsContent>
                )}
            </Tabs>
            </div>

            <InventoryDialogs
                inventoryDialog={inventoryDialog} setInventoryDialog={setInventoryDialog}
                inventoryDetailDialog={inventoryDetailDialog} setInventoryDetailDialog={setInventoryDetailDialog}
                receptionDialog={receptionDialog} setReceptionDialog={setReceptionDialog}
                deleteDialog={deleteDialog} setDeleteDialog={setDeleteDialog}
                deleteInventoryDialog={deleteInventoryDialog} setDeleteInventoryDialog={setDeleteInventoryDialog}
                deleteGroupDialog={deleteGroupDialog} setDeleteGroupDialog={setDeleteGroupDialog}
                deleteGroupConfirmDialog={deleteGroupConfirmDialog} setDeleteGroupConfirmDialog={setDeleteGroupConfirmDialog}
                inventoryFormPdfDialog={inventoryFormPdfDialog} setInventoryFormPdfDialog={setInventoryFormPdfDialog}
                inventoryForm={inventoryForm} setInventoryForm={setInventoryForm}
                inventoryItem={inventoryItem} setInventoryItem={setInventoryItem}
                detailAddItem={detailAddItem} setDetailAddItem={setDetailAddItem}
                receptionForm={receptionForm} setReceptionForm={setReceptionForm}
                editingInventory={editingInventory} setEditingInventory={setEditingInventory}
                inventoryToDelete={inventoryToDelete}
                selectedInventoryGroup={selectedInventoryGroup}
                groupToDelete={groupToDelete}
                deleteConfirmText={deleteConfirmText} setDeleteConfirmText={setDeleteConfirmText}
                addProductExpanded={addProductExpanded} setAddProductExpanded={setAddProductExpanded}
                pdfFormSiteId={pdfFormSiteId} setPdfFormSiteId={setPdfFormSiteId}
                pdfFormPeriod={pdfFormPeriod} setPdfFormPeriod={setPdfFormPeriod}
                sites={sites} products={products} sortedProducts={sortedProducts}
                receivedProductIds={receivedProductIds}
                supplierProducts={supplierProducts} periodOptions={periodOptions}
                handleAddInventoryItem={handleAddInventoryItem} handleRemoveInventoryItem={handleRemoveInventoryItem}
                handleSaveInventory={handleSaveInventory} resetInventoryForm={resetInventoryForm}
                handleAddProductFromDetail={handleAddProductFromDetail} handleSaveReception={handleSaveReception}
                handleDeleteMovement={handleDeleteMovement} handleConfirmDeleteInventory={handleConfirmDeleteInventory}
                handleFirstConfirmDeleteGroup={handleFirstConfirmDeleteGroup} handleConfirmDeleteGroup={handleConfirmDeleteGroup}
                handleDownloadInventoryFormPdf={handleDownloadInventoryFormPdf}
                handleEditInventory={handleEditInventory} handleDeleteInventoryClick={handleDeleteInventoryClick}
                getSupplierProductsForProduct={getSupplierProductsForProduct}
                calculateZoneTotal={calculateZoneTotal} calculateLineTotal={calculateLineTotal}
                renderInventoryRows={renderInventoryRows} renderInventoryCards={renderInventoryCards}
                getInvItemData={getInvItemData}
                canEdit={canEdit}
                inventoryFormTemplates={inventoryFormTemplates}
                onApplyTemplate={handleApplyTemplate}
                onApplyTemplateFromDetail={handleApplyTemplateFromDetail}
            />

            {/* Mini-dialog: choose site/period/type for a brand-new inventory.
                On submit, opens the SAME detail dialog as "Modifier" (grouped layout, inline edit, unit toggle, ...) */}
            <Dialog open={newInventoryDialog} onOpenChange={setNewInventoryDialog}>
                <DialogContent className="max-w-md p-4" data-testid="new-inventory-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-base">Nouvelle saisie d'inventaire</DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">
                            Choisissez le site, la période et le type. La saisie se fera ensuite dans la vue groupée par catégorie.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-2">
                        <div className="space-y-1">
                            <Label className="text-xs">Site *</Label>
                            <Select value={newInventoryForm.site_id} onValueChange={(v) => setNewInventoryForm({ ...newInventoryForm, site_id: v })}>
                                <SelectTrigger className="h-8 text-xs" data-testid="new-inventory-site"><SelectValue placeholder="Site" /></SelectTrigger>
                                <SelectContent>
                                    {sites.map(s => (
                                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Période *</Label>
                            <Select value={newInventoryForm.period} onValueChange={(v) => setNewInventoryForm({ ...newInventoryForm, period: v })}>
                                <SelectTrigger className="h-8 text-xs" data-testid="new-inventory-period"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {periodOptions.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Type *</Label>
                            <Select value={newInventoryForm.inventory_type} onValueChange={(v) => setNewInventoryForm({ ...newInventoryForm, inventory_type: v })}>
                                <SelectTrigger className="h-8 text-xs" data-testid="new-inventory-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="initial">Stock Initial</SelectItem>
                                    <SelectItem value="intermediate">Intermédiaire</SelectItem>
                                    <SelectItem value="final">Final</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" size="sm" onClick={() => setNewInventoryDialog(false)}>Annuler</Button>
                        <Button size="sm" onClick={handleStartInventoryFromForm} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="start-inventory-btn">
                            Commencer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <SupplierShortcutEditor
                open={showShortcutEditor}
                onClose={() => setShowShortcutEditor(false)}
                suppliers={suppliers}
                shortcutNames={supplierShortcutNames}
                onChange={setSupplierShortcutNames}
                defaults={STOCK_DEFAULT_SUPPLIER_SHORTCUTS}
                resolveShortcutSupplier={resolveShortcutSupplier}
            />
        </div>
    );
}

/**
 * Compact editor dialog for the user-editable supplier shortcut list (Stock page).
 */
function SupplierShortcutEditor({ open, onClose, suppliers, shortcutNames, onChange, defaults, resolveShortcutSupplier }) {
    const [draft, setDraft] = useState([]);
    const [newName, setNewName] = useState('');
    useEffect(() => {
        if (open) {
            setDraft([...shortcutNames]);
            setNewName('');
        }
    }, [open, shortcutNames]);
    const handleAdd = () => {
        const name = newName.trim();
        if (!name) return;
        if (draft.some(n => n.toLowerCase() === name.toLowerCase())) {
            toast.error('Ce fournisseur est déjà dans les raccourcis');
            return;
        }
        setDraft([...draft, name]);
        setNewName('');
    };
    const handleRemove = (idx) => setDraft(draft.filter((_, i) => i !== idx));
    const handleMove = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= draft.length) return;
        const copy = [...draft];
        [copy[idx], copy[target]] = [copy[target], copy[idx]];
        setDraft(copy);
    };
    const handleSave = () => { onChange(draft); onClose(); };
    const handleReset = () => setDraft([...defaults]);
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="inventory-shortcut-editor-dialog">
                <DialogHeader>
                    <DialogTitle>Raccourcis fournisseurs (Stock)</DialogTitle>
                    <DialogDescription>
                        Sélectionnez un fournisseur dans la liste, ou tapez un nom librement.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-72 overflow-auto py-1">
                    {draft.map((name, idx) => {
                        const supplier = resolveShortcutSupplier(name);
                        return (
                            <div key={`${name}-${idx}`} className="flex items-center gap-1 border rounded px-2 py-1">
                                <span className="flex-1 text-sm">
                                    {supplier?.name || name}
                                    {!supplier && <span className="text-xs text-red-500 ml-2">(introuvable)</span>}
                                </span>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => handleMove(idx, -1)} disabled={idx === 0}>↑</Button>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => handleMove(idx, 1)} disabled={idx === draft.length - 1}>↓</Button>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-500" onClick={() => handleRemove(idx)}>×</Button>
                            </div>
                        );
                    })}
                </div>
                <div className="flex gap-1">
                    <Select value="" onValueChange={(v) => v && setNewName(v)}>
                        <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Choisir un fournisseur…" /></SelectTrigger>
                        <SelectContent>
                            {suppliers
                                .filter(s => !draft.some(n => resolveShortcutSupplier(n)?.id === s.id))
                                .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
                                .map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)
                            }
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex gap-1">
                    <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Ou taper un nom..." className="h-8 text-xs" onKeyDown={(e) => e.key === 'Enter' && handleAdd()} />
                    <Button size="sm" onClick={handleAdd} className="h-8 text-xs">Ajouter</Button>
                </div>
                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={handleReset}>Réinitialiser</Button>
                    <Button variant="outline" size="sm" onClick={onClose}>Annuler</Button>
                    <Button size="sm" onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600 text-white">Enregistrer</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
